/**
 * @file modules/my_cash_store_autocomplete.js
 * @description Vanilla JS autocomplete for store search in the Missing Cash modal.
 *              Fetches stores from the stores endpoint and displays suggestions.
 *              Uses a MutationObserver to bind events once fragno-input renders
 *              its inner <input> (Light DOM, async rendering).
 */

/**
 * Exported so tests have a seam to drive the module directly, matching
 * my_cash_missing_form.js. The module still self-initializes at the bottom of the file.
 */
export function initStoreAutocomplete() {
  const container = document.querySelector(".my-cash-store-autocomplete");
  if (!container) return;

  const fragnoInput = container.querySelector("fragno-input");
  const hiddenField = container.querySelector(
    ".my-cash-store-autocomplete__value",
  );
  const results = container.querySelector(
    ".my-cash-store-autocomplete__results",
  );
  const url = container.dataset.url;

  if (!fragnoInput || !hiddenField || !results || !url) return;

  let debounceTimer = null;
  let bound = false;

  function getNativeInput() {
    return (
      fragnoInput.querySelector("input.fragno-input") ||
      fragnoInput.querySelector("input")
    );
  }

  function bindInputEvents() {
    if (bound) return;
    const nativeInput = getNativeInput();
    if (!nativeInput) return;
    bound = true;

    nativeInput.addEventListener("input", () => {
      const query = nativeInput.value.trim();
      clearTimeout(debounceTimer);

      if (query.length < 2) {
        closeResults();
        hiddenField.value = "";
        return;
      }

      debounceTimer = setTimeout(() => fetchStores(query), 300);
    });
  }

  async function fetchStores(query) {
    results.innerHTML =
      '<div class="my-cash-store-autocomplete__loading">Searching...</div>';
    results.classList.add("my-cash-store-autocomplete__results--open");

    try {
      const response = await fetch(
        `${url}.json?q=${encodeURIComponent(query)}&search_funnel=off`,
        { headers: { Accept: "application/json" } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const stores = data.results || data;

      if (!Array.isArray(stores) || stores.length === 0) {
        closeResults();
        return;
      }

      results.replaceChildren(
        ...stores.slice(0, 10).map((store) => {
          const option = document.createElement("div");
          option.className = "my-cash-store-autocomplete__option";
          option.dataset.id = store.id;
          option.textContent = store.name;
          return option;
        }),
      );

      results.classList.add("my-cash-store-autocomplete__results--open");
    } catch {
      closeResults();
    }
  }

  function closeResults() {
    results.classList.remove("my-cash-store-autocomplete__results--open");
    results.innerHTML = "";
  }

  // Select a result
  results.addEventListener("click", (event) => {
    const option = event.target.closest(".my-cash-store-autocomplete__option");
    if (!option) return;

    hiddenField.value = option.dataset.id;
    const nativeInput = getNativeInput();
    if (nativeInput) {
      nativeInput.value = option.textContent;
    }
    closeResults();
  });

  // Close on outside click
  document.addEventListener("click", (event) => {
    if (!container.contains(event.target)) {
      closeResults();
    }
  });

  // fragno-input renders its inner <input> asynchronously after connectedCallback.
  // Use a MutationObserver to bind once the native input appears.
  const observer = new MutationObserver(() => {
    if (getNativeInput()) {
      bindInputEvents();
      observer.disconnect();
    }
  });
  observer.observe(fragnoInput, { childList: true, subtree: true });

  // Also try immediately in case it's already rendered
  bindInputEvents();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initStoreAutocomplete);
} else {
  initStoreAutocomplete();
}
